import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { formatUsd } from '../components/charts/chartUtils';
import DashboardLayout from '../components/DashboardLayout';
import LogDetailModal from '../components/history/LogDetailModal';
import { ModelNameWithDetail } from '../components/ModelNameWithDetail';
import { useProject } from '../contexts/ProjectContext';
import { dashboardService } from '../services/dashboard';
import { parsePaginatedResponse } from '../utils/pagination';

const PAGE_SIZE = 10;

const COLUMNS = [
  { key: 'create_date', label: 'Request' },
  { key: 'decision', label: 'Status' },
  { key: 'input_model', label: 'Model' },
  { key: 'latency_ms', label: 'Latency' },
  { key: 'tokens', label: 'Tokens' },
  { key: 'cost_usd', label: 'Cost' },
  { key: 'eval', label: 'Eval', sortable: false },
];

const STATUS_META = {
  routed: { label: 'Routed', icon: '⇄', className: 'text-emerald-400' },
  cache_hit: { label: 'Cached', icon: '⚡', className: 'text-sky-400' },
  passthrough: { label: 'Passthrough', icon: '↪', className: 'text-amber-400' },
  error: { label: 'Error', icon: '⚠', className: 'text-red-500' },
};

const statusKey = (log) => {
  if (log.decision === 'cache_hit' || log.cache_hit) return 'cache_hit';
  if (log.decision === 'local' || log.decision === 'fallback') return 'passthrough';
  if (log.decision === 'error') return 'error';
  return 'routed';
};

const formatTps = (log) => {
  const genMs = log.generation_ms || 0;
  const tokensOut = log.tokens_out || 0;
  if (!genMs || !tokensOut) return null;
  return `${Math.round((tokensOut * 1000) / genMs)} tps`;
};

const formatOverhead = (log) => {
  const ms = log.latency_ms || 0;
  if (!ms) return null;
  return `+${ms}ms`;
};

const evalComparison = (delta) => {
  if (delta == null) return null;
  if (delta > 2) return { label: 'better', className: 'text-emerald-400' };
  if (delta < -2) return { label: 'worse', className: 'text-red-400' };
  return { label: 'equal', className: 'opacity-60' };
};

const finalModel = (log) => log.output_model || log.input_model;

export default function HistoryPage() {
  const { selectedHash } = useProject();
  const navigate = useNavigate();
  const location = useLocation();
  const { eventHash } = useParams();
  const detailTab = location.pathname.endsWith('/evals/') ? 'evals' : 'request';
  const [logs, setLogs] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('create_date');
  const [order, setOrder] = useState('desc');
  const [apiKey, setApiKey] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [keyOptions, setKeyOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [runningEval, setRunningEval] = useState(false);
  const [evalError, setEvalError] = useState('');

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const loadLogs = useCallback(() => {
    setLoading(true);
    dashboardService.logs(
      selectedHash,
      {
        page,
        page_size: PAGE_SIZE,
        sort,
        order,
        api_key: apiKey,
        status: statusFilter,
        model: modelFilter,
      },
      (res) => {
        const { results, count: total } = parsePaginatedResponse(res);
        setLogs(results);
        setCount(total);
        setKeyOptions(res?.data?.filter_options?.keys || []);
        setModelOptions(res?.data?.filter_options?.models || []);
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, [page, sort, order, selectedHash, apiKey, statusFilter, modelFilter]);

  useEffect(() => {
    setApiKey('');
    setStatusFilter('');
    setModelFilter('');
    setPage(1);
  }, [selectedHash]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    if (!eventHash) {
      setDetail(null);
      setEvalError('');
      return;
    }
    setDetailLoading(true);
    setEvalError('');
    dashboardService.logDetail(
      eventHash,
      (res) => {
        setDetail(res.data);
        setDetailLoading(false);
      },
      () => {
        setDetailLoading(false);
        navigate('/history/', { replace: true });
      },
    );
  }, [eventHash, navigate]);

  const toggleSort = (field) => {
    if (sort === field) {
      setOrder((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(field);
      setOrder(field === 'input_model' ? 'asc' : 'desc');
    }
    setPage(1);
  };

  const sortIndicator = (field) => {
    if (sort !== field) return '';
    return order === 'asc' ? ' ↑' : ' ↓';
  };

  const openDetail = (hash) => navigate(`/history/${hash}/`);

  const closeDetail = () => navigate('/history/');

  const setDetailTab = (tab) => {
    if (!eventHash) return;
    navigate(tab === 'evals' ? `/history/${eventHash}/evals/` : `/history/${eventHash}/`);
  };

  const handleRunEval = () => {
    if (!detail?.id || !selectedHash || !eventHash) return;
    setRunningEval(true);
    setEvalError('');
    navigate(`/history/${eventHash}/evals/`);
    dashboardService.runEval(
      selectedHash,
      { usage_event_id: detail.id },
      (res) => {
        setRunningEval(false);
        dashboardService.logDetail(
          eventHash,
          (detailRes) => setDetail(detailRes.data),
          () => {},
        );
        loadLogs();
      },
      (err) => {
        setEvalError(err?.response?.data?.detail || err?.detail || 'Eval failed');
        setRunningEval(false);
      },
    );
  };

  return (
    <DashboardLayout title="History">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-3 text-sm">
          <label>
            <span className="opacity-60 mr-2">API key</span>
            <select
              value={apiKey}
              onChange={(e) => { setApiKey(e.target.value); setPage(1); }}
              className="rounded-lg border border-theme-secondary bg-theme-main px-2 py-1"
            >
              <option value="">All</option>
              <option value="playground">Playground</option>
              {keyOptions.map((k) => (
                <option key={k.hash} value={k.hash}>{k.name}</option>
              ))}
            </select>
          </label>
          <label>
            <span className="opacity-60 mr-2">Status</span>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="rounded-lg border border-theme-secondary bg-theme-main px-2 py-1"
            >
              <option value="">All</option>
              <option value="routed">Routed</option>
              <option value="cached">Cached</option>
              <option value="passthrough">Passthrough</option>
              <option value="error">Error</option>
            </select>
          </label>
          <label>
            <span className="opacity-60 mr-2">Model</span>
            <select
              value={modelFilter}
              onChange={(e) => { setModelFilter(e.target.value); setPage(1); }}
              className="rounded-lg border border-theme-secondary bg-theme-main px-2 py-1"
            >
              <option value="">All</option>
              {modelOptions.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>
        </div>
        <button
          type="button"
          onClick={loadLogs}
          disabled={loading}
          className="px-4 py-2 rounded-lg border border-theme-secondary text-sm opacity-80 hover:opacity-100 disabled:opacity-40"
        >
          Refresh
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-theme-secondary">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-theme-secondary bg-theme-secondary/30 text-left text-xs uppercase tracking-wide opacity-60">
              {COLUMNS.map((column) => (
                <th key={column.key} className="px-4 py-3 font-medium whitespace-nowrap">
                  {column.sortable === false ? (
                    <span>{column.label}</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggleSort(column.key)}
                      className="text-left hover:text-theme-primary"
                    >
                      {column.label}
                      {sortIndicator(column.key)}
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && logs.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className="px-4 py-8 text-center opacity-50">
                  Loading...
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} className="px-4 py-8 text-center opacity-50">
                  No requests logged yet.
                </td>
              </tr>
            ) : (
              logs.map((log) => {
                const comparison = evalComparison(log.eval_delta);
                const totalTokens = (log.tokens_in || 0) + (log.tokens_out || 0);
                const tps = formatTps(log);
                const overhead = formatOverhead(log);
                const hasSavings = Number(log.savings_usd || 0) > 0;
                return (
                  <tr
                    key={log.hash}
                    onClick={() => openDetail(log.hash)}
                    className="border-b border-theme-secondary/50 last:border-b-0 cursor-pointer hover:bg-theme-secondary/20"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-xs">
                      <div className="font-medium">{new Date(log.create_date).toLocaleString()}</div>
                      <div className="opacity-50 mt-0.5">{log.key_name || 'API'}</div>
                    </td>
                    <td className="px-4 py-3"><StatusCell log={log} /></td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">
                      <div>
                        <ModelNameWithDetail compact model={finalModel(log)} emphasis="routed" />
                      </div>
                      <div className="mt-0.5 opacity-50">
                        via{' '}
                        <ModelNameWithDetail compact model={log.input_model} emphasis="original" />
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      <div>{tps || '—'}</div>
                      {overhead && (
                        <div className="opacity-50 mt-0.5 font-sans">{overhead}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {totalTokens.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">
                      <div>{formatUsd(log.cost_usd)}</div>
                      {hasSavings ? (
                        <div className="text-theme-primary mt-0.5 font-sans">
                          {Math.round(log.savings_pct)}% saved
                        </div>
                      ) : (
                        <div className="opacity-40 mt-0.5 font-sans">no savings</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {log.eval_score != null ? (
                        <>
                          <div className="font-mono font-medium">{Math.round(log.eval_score)}</div>
                          {comparison && (
                            <div className={comparison.className}>{comparison.label}</div>
                          )}
                        </>
                      ) : (
                        <span className="opacity-40">--</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4 text-sm">
        <span className="opacity-60">
          Page {page} of {totalPages} ({count} total)
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            disabled={page <= 1 || loading}
            className="px-3 py-1 rounded-lg border border-theme-secondary disabled:opacity-40"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            disabled={page >= totalPages || loading}
            className="px-3 py-1 rounded-lg border border-theme-secondary disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>

      {eventHash && (
        <LogDetailModal
          detail={detail}
          loading={detailLoading}
          tab={detailTab}
          onTabChange={setDetailTab}
          runningEval={runningEval}
          evalError={evalError}
          onClose={closeDetail}
          onRunEval={handleRunEval}
        />
      )}
    </DashboardLayout>
  );
}

function StatusCell({ log }) {
  const meta = STATUS_META[statusKey(log)] || STATUS_META.routed;
  return (
    <span className={`inline-flex items-center gap-1.5 ${meta.className}`}>
      <span aria-hidden>{meta.icon}</span>
      <span className="text-xs font-medium">{meta.label}</span>
    </span>
  );
}
